// 다음 성장 목표 모델: 마을·결과·패배 화면이 같은 실제 데이터(장비 6부위 가방·마을 장비 상점·VILLAGE_NPCS, deriveStats, 지역 기록)에서 문구를 만든다.
// 장비 통합(ECONOMY 14절): 추천 장비는 가방의 더 좋은 장비(무료 장착)와 지금 마을 상점의 6부위 장비(구매)다. 옛 상점 무기·갑옷은 팔지 않는다.
// 순수 로직이다. DOM·three를 모르고, 골드·장비·기록을 바꾸지 않는다. 가격·보상·판정은 새로 정하지 않는다.
import { STARS } from '../shared/config';
import type { GrowthMods } from '../shared/growth';
import { REGION_IDS, type RegionId } from '../shared/levels';
import { REGIONS } from '../shared/regions';
import { VILLAGE_NPCS, deriveStats, makeStats, townNpcByRole, type GearStatsIn, type NpcDef, type NpcRole } from '../shared/rpg';
import { equipDef, makeGearTotals, sumGear, weaponUsableBy, type GearItem, type GearTotals } from '../shared/gear';
import { SLOT_ORDER } from '../shared/gearLedger';
import { WEAPON_EQUIP_HEROES, offerName, townGearBuyableBy, townGearFor, townGearItem, townGearPrice } from '../shared/content/townShop';
import type { EquipSlot, HeroId } from '../shared/content/types';
import { gearIconPath } from './gearModel';
import type { RegionRecordView } from '../shared/types';
import { fmtNum } from './dom';
import { STAT_LABEL, fmtStat, modeNotice, type ProfileLike, type StatDelta, type StatKey } from './rpgModel';
import { fmtBestTime, starsText } from './regionModel';

export type RecordsLike = Readonly<Partial<Record<RegionId, RegionRecordView | undefined>>>;
export type RecordGoalKind = 'first' | 'stars' | 'time';

/** 추천 장비(가방 개체 또는 마을 상점 품목) */
export interface GoalItem {
  /** 가방 개체 uid 또는 상점 품목 id */
  readonly id: string;
  readonly name: string;
  /** 상점 가격(가방 장비는 0) */
  readonly price: number;
  /** 아이콘 그림 경로(public/assets 기준) */
  readonly icon: string;
  readonly seller: NpcRole | null;
}
/** 목표 계산에 필요한 장비 장부 필드만 */
export type GearBag = { readonly items: readonly GearItem[]; readonly equipped: Readonly<Record<EquipSlot, string | null>>; readonly heroWeapons?: Readonly<Partial<Record<HeroId, string>>> };

/** 가방에서 한 번에 장착할 수 있는 더 좋은 장비(부위마다 가장 좋은 하나) */
export interface EquipUpgrade { readonly uid: string; readonly slot: EquipSlot; readonly name: string }

export interface GrowthGoal {
  /** equip: 가진 장비를 무료로 장착 / buy: 판매 NPC에게서 구매 / record: 실제 지역 기록 목표 / none: 보여 줄 목표 없음 */
  kind: 'equip' | 'buy' | 'record' | 'none';
  /** 프로필 모드가 ok가 아니면 true: 구매 가능이라 말하지 않고 이유를 보인다 */
  locked: boolean;
  item: GoalItem | null;
  /** 장착했을 때 실제 능력치 변화(오른 값만 있는 장비만 추천된다) */
  deltas: StatDelta[];
  /** 확정 골드(profile.gold). 저장 대기 골드는 제외 */
  gold: number;
  pendingGold: number;
  /** 모자란 골드(0이면 지금 살 수 있는 금액) */
  shortage: number;
  seller: NpcDef | null;
  recordKind: RecordGoalKind | null;
  region: RegionId | null;
  /** 지역 기록 목표의 현재 값(예: ★☆☆, 04:12). 없으면 '' */
  recordNow: string;
  /** 잠금 사유(modeNotice 문구). 잠기지 않았으면 '' */
  notice: string;
  /** equip 목표일 때: 「추천 장비 모두 장착」으로 한 번에 낄 장비(부위마다 하나). 아니면 빈 배열 */
  upgrades: readonly EquipUpgrade[];
}

const NONE: GrowthGoal = { kind: 'none', locked: false, item: null, deltas: [], gold: 0, pendingGold: 0, shortage: 0, seller: null, recordKind: null, region: null, recordNow: '', notice: '', upgrades: [] };

/** 방향 낱말: 마을 지도에서 북쪽이 -z, 동쪽이 +x다(대장간 북동, 잡화상 북서, 교관 남서) */
export function npcDirection(npc: Pick<NpcDef, 'x' | 'z'>): string {
  return (npc.z < 0 ? '북' : '남') + (npc.x > 0 ? '동' : '서') + '쪽';
}
/** 그 마을에서 그 물건을 파는 NPC(자리는 마을마다 다르다, S7) */
function sellerNpc(role: NpcRole | null, town = 1): NpcDef | null {
  return role ? townNpcByRole(town, role) ?? VILLAGE_NPCS.find((n) => n.role === role) ?? null : null;
}
/** "대장장이 브론(북동쪽)" */
export function npcWhere(npc: NpcDef): string {
  return npc.title + ' ' + npc.name + '(' + npcDirection(npc) + ')';
}

const STAT_KEYS: readonly StatKey[] = ['attack', 'maxHp', 'maxMp', 'critChance'];
const _now = makeStats(), _next = makeStats();
const _tNow = makeGearTotals(), _tNext = makeGearTotals();
const TOTAL_KEYS = (Object.keys(_tNow) as (keyof GearTotals)[]).filter((k) => k !== 'armorTier' && k !== 'weaponTier');
/**
 * 그 장비를 그 부위에 끼면 바뀌는 실제 능력치(deriveStats: 기본 + 장비 6부위 + 성장 + 주력). 지금 장착과 후보 장착을 같은 방식으로 더해 비교한다.
 * 모든 값이 오르고 하나라도 바뀌면 그 변화, 아니면 null(다운그레이드·옆걸음은 추천하지 않는다). 다른 영웅 전용 무기는 세지 않는다(c022, lyra-weapon-damage)
 */
function strictUpgrade(p: ProfileLike, bag: GearBag, hero: HeroId, slot: EquipSlot, cand: GearItem, mods?: GrowthMods): StatDelta[] | null {
  const eq: (GearItem | null)[] = SLOT_ORDER.map((k) => { const u = bag.equipped[k]; return u ? bag.items.find((x) => x.uid === u) ?? null : null; });
  if (!weaponUsableBy(eq[0], hero)) eq[0] = null;
  const focus = hero === 'kael' ? p.skillFocus : 0;
  deriveStats(_now, focus, mods, sumGear(_tNow, eq));
  const i = SLOT_ORDER.indexOf(slot);
  const keep = eq[i];
  // 후보 무기는 지금 실제로 낄 수 있어야 한다(라이라 무기 칸이 열리기 전에는 gearEquip이 wrong_hero로 거절 — Codex P2 #347). 이미 낀 무기의 능력치 반영과 별개
  if (slot === 'weapon' && !(WEAPON_EQUIP_HEROES.includes(hero) && weaponUsableBy(cand, hero))) return null;
  eq[i] = cand;
  deriveStats(_next, focus, mods, sumGear(_tNext, eq));
  eq[i] = keep;
  const out: StatDelta[] = [];
  for (const key of STAT_KEYS) {
    const from = _now[key], to = _next[key];
    if (Math.abs(from - to) < 1e-9) continue;
    out.push({ key, label: STAT_LABEL[key], from: fmtStat(key, from), to: fmtStat(key, to), up: to > from });
  }
  if (out.length === 0 || !out.every((x) => x.up)) return null;
  // 능력치 줄에 안 보이는 전투 보정(스킬 피해·흡혈·세트 효과 등)이 하나라도 줄면 맞바꿈이라 목표로 권하지 않는다(PR #205 Codex P2).
  // 모든 칸은 클수록 좋다(받는 피해·재사용 대기는 감소량으로 센다). 외형 단계는 능력치가 아니다
  for (const k of TOTAL_KEYS) if (_tNext[k] < _tNow[k] - 1e-9) return null;
  return out;
}

function count(n: number | undefined): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/** 모든 실제 업그레이드를 장착한 뒤의 목표: 첫 클리어 → 별 3개 → 최단 기록, 각각 이끼 유적 → 잿불 협곡 순 */
function recordGoal(base: GrowthGoal, records: RecordsLike, open?: (id: RegionId) => boolean): GrowthGoal {
  const pass = (kind: RecordGoalKind): GrowthGoal | null => {
    for (const id of REGION_IDS) {
      // 열린 지역만(D3a 순차 해금). 진행을 모르면 이전처럼 이끼 유적·잿불 협곡만
      if (open ? !open(id) : id !== 'r1' && id !== 'r2') continue;
      const rec = records[id];
      const clears = count(rec?.clears), best = count(rec?.bestClearTicks), stars = Math.min(3, count(rec?.bestStars));
      const cleared = clears > 0 || best > 0 || stars > 0;
      if (kind === 'first' && !cleared) return { ...base, kind: 'record', recordKind: kind, region: id, recordNow: '' };
      if (kind === 'stars' && stars < 3) return { ...base, kind: 'record', recordKind: kind, region: id, recordNow: starsText(stars) };
      if (kind === 'time' && best > 0) return { ...base, kind: 'record', recordKind: kind, region: id, recordNow: fmtBestTime(best) };
    }
    return null;
  };
  return pass('first') ?? pass('stars') ?? pass('time') ?? base;
}

/**
 * 지금 프로필에서 한 가지 다음 목표를 고른다(장비 통합, ECONOMY 14절).
 * 1) 가방에 있고 지금 장착보다 엄격히 좋아지는 장비(무료 장착 — 공격이 가장 많이 오르는 것), 2) 지금 마을 상점의 6부위 장비 중
 * 엄격히 좋아지는 것을 부족 골드가 적은 순 → 낮은 가격 순. 구매 가능액은 gold만 본다(pendingGold 제외). 둘 다 없으면 지역 기록 목표.
 * bag(장비 장부)이 없으면 장비 목표는 건너뛴다.
 */
export function growthGoal(p: ProfileLike, records: RecordsLike, mods?: GrowthMods, _gear?: GearStatsIn, open?: (id: RegionId) => boolean, town = 1, bag?: GearBag, hero: HeroId = 'kael'): GrowthGoal {
  const locked = p.mode !== 'ok';
  const notice = locked ? modeNotice(p.mode)?.text ?? '' : '';
  const base: GrowthGoal = { ...NONE, locked, notice, gold: p.gold, pendingGold: p.pendingGold };
  if (bag) {
    const worn = new Set(SLOT_ORDER.map((k) => bag.equipped[k]).filter((u): u is string => !!u));
    // 다른 영웅 무기 칸에 낀 무기는 추천 후보에서 뺀다(빼앗아 끼우지 않는다)
    for (const u of Object.values(bag.heroWeapons ?? {})) if (u) worn.add(u);
    let free: { it: GearItem; d: StatDelta[]; gain: number } | null = null;
    // 부위마다 가장 좋은 업그레이드(추천 장비 모두 장착)
    const bySlot = new Map<EquipSlot, { it: GearItem; gain: number }>();
    for (const it of bag.items) {
      if (worn.has(it.uid)) continue;
      const d = equipDef(it.def);
      if (!d || (d.heroLock && d.heroLock !== hero)) continue;
      const dd = strictUpgrade(p, bag, hero, d.slot, it, mods);
      if (!dd) continue;
      const gain = _next.attack - _now.attack + (_next.maxHp - _now.maxHp) / 10;
      if (!free || gain > free.gain) free = { it, d: dd, gain };
      const best = bySlot.get(d.slot);
      if (!best || gain > best.gain) bySlot.set(d.slot, { it, gain });
    }
    if (free) {
      const it = free.it;
      const upgrades: EquipUpgrade[] = [];
      for (const k of SLOT_ORDER) { const b = bySlot.get(k); if (b) upgrades.push({ uid: b.it.uid, slot: k, name: itemNameOf(b.it) }); }
      return { ...base, kind: 'equip', item: { id: it.uid, name: itemNameOf(it), price: 0, icon: gearIconPath(it), seller: null }, deltas: free.d, upgrades };
    }
    let buy: { item: GoalItem; d: StatDelta[]; short: number } | null = null;
    for (const role of ['forge', 'shop'] as const) {
      for (const o of townGearFor(town, role)) {
        if (!townGearBuyableBy(o, hero)) continue;
        const cand = townGearItem(o, hero, 'goal');
        const dd = strictUpgrade(p, bag, hero, o.slot, cand, mods);
        if (!dd) continue;
        const price = townGearPrice(o), short = Math.max(0, price - p.gold);
        if (!buy || short < buy.short || (short === buy.short && price < buy.item.price)) {
          buy = { item: { id: o.id, name: offerName(o, hero), price, icon: gearIconPath(cand), seller: o.seller }, d: dd, short };
        }
      }
    }
    if (buy) return { ...base, kind: 'buy', item: buy.item, deltas: buy.d, shortage: buy.short, seller: sellerNpc(buy.item.seller, town) };
  }
  return recordGoal(base, records, open);
}
/** 가방 개체 이름(정의 이름 + 강화 단계) */
function itemNameOf(it: GearItem): string {
  const d = equipDef(it.def);
  return (d ? d.name : '장비') + (it.enh > 0 ? ' +' + it.enh : '');
}

/** "공격력 2,000 → 2,200 · 치명률 15% → 18%" */
export function deltaText(d: readonly StatDelta[]): string {
  return d.map((x) => x.label + ' ' + x.from + ' → ' + x.to).join(' · ');
}
/** 목표 장비 이름 + 할 일(장착/구매): 처음 온 이용자가 문구만 보고 무엇을 하라는 건지 알게 한다 */
export function goalItemName(g: GrowthGoal): string {
  return (g.item ? g.item.name : '') + (g.kind === 'equip' ? ' 장착' : ' 구매');
}
const STAR_TERMS = '클리어 · ' + Math.round(STARS.timeTicks / 3600) + '분 이내 · HP ' + Math.round(STARS.healthRatio * 100) + '% 이상';

/** 저장 대기 골드는 쓸 수 없으므로 따로 밝힌다 */
function pendingNote(pendingGold: number): string {
  return pendingGold > 0 ? ' (저장 대기 +' + fmtNum(pendingGold) + ' G 제외)' : '';
}

/** 마을·결과가 쓰는 두 줄. 목표가 없으면 null. touch는 장비 창을 여는 안내 글자만 바꾼다 */
export function goalLines(g: GrowthGoal, touch: boolean): [string, string] | null {
  if (g.kind === 'none') return null;
  if (g.kind === 'record') {
    // 문 자리는 마을마다 달라(STAGE-WORLD-PRD 5.4) 방위를 박지 않고 지역 이름으로 말한다(퀘스트 창 문 줄이 방위·자동 이동을 준다)
    const info = g.region ? REGIONS[g.region] : null;
    if (!info) return null;
    if (g.recordKind === 'first') return ['다음 목표 · ' + info.name + ' 첫 클리어', info.name + '(' + info.tier + ') 던전 문으로 들어가 도전하세요'];
    if (g.recordKind === 'stars') return ['다음 목표 · ' + info.name + ' 별 3개 (지금 ' + g.recordNow + ')', '별 조건: ' + STAR_TERMS];
    return ['다음 목표 · ' + info.name + ' 최단 기록 갱신 (지금 ' + g.recordNow + ')', info.name + ' 던전 문에서 더 빨리 클리어하세요'];
  }
  const it = g.item;
  if (!it) return null;
  const head = '다음 목표 · ' + goalItemName(g) + ' · ' + deltaText(g.deltas);
  if (g.locked) return [head, g.notice];
  // 무엇을 눌러야 하는지 그대로 쓴다: 가방 열기 → 이 장비 고르기 → [장착하기]
  if (g.kind === 'equip') return [head, '이미 가졌어요 · ' + (touch ? '가방 버튼' : '가방(B)') + ' → \'' + it.name + '\' 선택 → [장착하기] 누르기'];
  const where = g.seller ? ' · ' + npcWhere(g.seller) : '';
  const gold = '확정 골드 ' + fmtNum(g.gold) + ' G';
  const verb = '구매';
  const money = g.shortage > 0 ? gold + ' · ' + fmtNum(g.shortage) + ' G 모자라요' : gold + '로 ' + verb + ' 가능';
  return [head, fmtNum(it.price) + ' G · ' + money + pendingNote(g.pendingGold) + where];
}

/** 결과 화면 부제. 카드를 고르기 전에는 카드 선택을 먼저 안내한다(버튼 게이트는 screens가 그대로 유지) */
export function resultLines(g: GrowthGoal, picked: boolean, touch: boolean): string {
  if (!picked) return '보상 카드를 먼저 고르면 다음 성장 목표를 알려 드려요.';
  const lines = goalLines(g, touch);
  return lines ? lines[0] + '\n' + lines[1] : '';
}

/** 결과의 '마을로' 버튼 글자: 마을에서 실제로 할 일(장착·구매)이 있고 잠기지 않았을 때만 바꾼다 */
export function villageButtonLabel(g: GrowthGoal, picked: boolean): string {
  return picked && !g.locked && (g.kind === 'equip' || g.kind === 'buy') ? '마을에서 준비' : '마을로';
}

/** 패배 화면 보조 문구: 확정 골드·보유 장비 유지, 무료 주력 변경 경로, 다음 목표. 이번 판 임시 보상은 말하지 않는다 */
export function defeatLines(p: ProfileLike, g: GrowthGoal): string[] {
  const out: string[] = [];
  out.push('확정 골드 ' + fmtNum(p.gold) + ' G' + pendingNote(p.pendingGold) + '와 보유 장비는 이번 패배로 줄지 않아요.');
  const trainer = VILLAGE_NPCS.find((n) => n.role === 'trainer');
  if (trainer) {
    out.push(p.mode === 'ok'
      ? '마을 ' + npcWhere(trainer) + '에게서 주력 스킬을 무료로 바꿀 수 있어요.'
      : '주력 스킬 변경은 지금 쉬어요 · ' + (modeNotice(p.mode)?.text ?? ''));
  }
  const lines = goalLines(g, false);
  if (lines && !g.locked) out.push(lines[0]);
  return out;
}

export type GrowthProfile = ProfileLike & { readonly revision: number };
/** 바뀔 때만 다시 만들게 하는 변경 추적기. 숫자·참조만 비교하고 프레임마다 객체를 만들지 않는다 */
export class GrowthTracker {
  /** 입력이 바뀐 횟수. 화면 조각은 자기 마지막 version과 비교해 다시 쓴다 */
  version = 0;
  private gRev = -1;
  private scene = -1; private picked = false; private rev = -1; private mode = ''; private gold = -1; private pend = -1;
  private bag: GearBag | undefined = undefined; private hero = '';
  private readonly rec = new Float64Array(REGION_IDS.length * 3);
  private cached: GrowthGoal = NONE;
  private gearRev = 0;
  /** 합계 객체가 바뀌면(영웅 교체: 카엘 합계 ↔ 무기 제외 합계) 목표를 다시 계산한다 */
  private gearObj: GearStatsIn | undefined = undefined;
  private pRev = -1;
  private town = 1;

  get goal(): GrowthGoal { return this.cached; }

  /** 입력이 바뀌었으면 version을 올리고 목표를 다시 계산해 true. 같으면 false(할당 없음) */
  sync(scene: number, picked: boolean, p: GrowthProfile, records: RecordsLike, mods?: GrowthMods, growthRev = 0, gear?: GearStatsIn, gearRev = 0, open?: (id: RegionId) => boolean, progressRev = 0, town = 1, bag?: GearBag, hero: HeroId = 'kael'): boolean {
    let dirty = town !== this.town || gear !== this.gearObj || progressRev !== this.pRev || growthRev !== this.gRev || gearRev !== this.gearRev || scene !== this.scene || picked !== this.picked || p.revision !== this.rev || p.mode !== this.mode || p.gold !== this.gold ||
      p.pendingGold !== this.pend || bag !== this.bag || hero !== this.hero;
    for (let i = 0; i < REGION_IDS.length; i++) {
      const r = records[REGION_IDS[i]];
      const c = count(r?.clears), t = count(r?.bestClearTicks), b = count(r?.bestStars);
      if (this.rec[i * 3] !== c || this.rec[i * 3 + 1] !== t || this.rec[i * 3 + 2] !== b) { dirty = true; this.rec[i * 3] = c; this.rec[i * 3 + 1] = t; this.rec[i * 3 + 2] = b; }
    }
    if (!dirty) return false;
    this.gRev = growthRev; this.gearRev = gearRev; this.gearObj = gear; this.pRev = progressRev; this.town = town;
    this.scene = scene; this.picked = picked; this.rev = p.revision; this.mode = p.mode; this.gold = p.gold; this.pend = p.pendingGold;
    this.bag = bag; this.hero = hero;
    this.cached = growthGoal(p, records, mods, gear, open, town, bag, hero);
    this.version++;
    return true;
  }
}
